import { useEffect, useRef, useState } from "react";
import { getConditionalFieldsToClean } from "@helpers/clean-conditional-filters";

const STORAGE_KEY_PREFIX = "table_filter_values_";

type AnyObj = Record<string, any>;

function parseStoredObject(raw: string | null): AnyObj {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return {};
    return parsed as AnyObj;
  } catch {
    return {};
  }
}

export function useFilterListWithRequiredValues<T extends AnyObj>(
  tableKey: string,
  defaultFilter: T,
  excludeFromStorage?: string[],
  sid?: string | number,
  allowedFields?: string[],
  fields?: Array<{
    field: string;
    conditional?: {
      field: string;
      value: string;
    };
  }>,
) {
  const storageKey = STORAGE_KEY_PREFIX + tableKey + (sid ? `_sc_${sid}` : "");
  const storageKeyOptions = storageKey + "_options";
  const filterIdsKey = "table_filters_" + tableKey;

  const [filterList, setFilterList] = useState<T>(() => {
    const raw = localStorage.getItem(storageKey);
    const filterIdsRaw = localStorage.getItem(filterIdsKey);

    if (!raw) {
      return { ...defaultFilter } as T;
    }

    const stored = parseStoredObject(raw);

    if (fields && fields.length > 0) {
      const fieldsToClean = getConditionalFieldsToClean(fields, stored);

      if (fieldsToClean.length > 0) {
        fieldsToClean.forEach((fieldName) => {
          delete stored[fieldName];
        });
        localStorage.setItem(storageKey, JSON.stringify(stored));

        const storedOptions = localStorage.getItem(storageKeyOptions);
        if (storedOptions) {
          try {
            const parsedOptions = JSON.parse(storedOptions);
            fieldsToClean.forEach((fieldName) => {
              delete parsedOptions[fieldName];
            });
            localStorage.setItem(
              storageKeyOptions,
              JSON.stringify(parsedOptions),
            );
          } catch {
            return { ...stored } as T;
          }
        }
      }
    }

    let activeFilterIds: string[] = [];
    try {
      const parsed = JSON.parse(filterIdsRaw || "[]");
      activeFilterIds = Array.isArray(parsed) ? parsed : [];
    } catch {
      return { ...stored } as T;
    }

    const result = { ...stored } as T;
    activeFilterIds.forEach((filterId) => {
      if (filterId in defaultFilter && !(filterId in result)) {
        (result as AnyObj)[filterId] = defaultFilter[filterId];
      }
    });

    return result;
  });

  const [filterOptions, setFilterOptions] = useState<Record<string, any>>(
    () => {
      return parseStoredObject(localStorage.getItem(storageKeyOptions));
    },
  );

  const lastSavedRef = useRef<string>("");
  const lastSavedOptionsRef = useRef<string>("");

  useEffect(() => {
    const toSave = { ...filterList } as AnyObj;
    if (excludeFromStorage) {
      excludeFromStorage.forEach((field) => {
        delete toSave[field];
      });
    }

    const sig = JSON.stringify(toSave);
    if (sig !== lastSavedRef.current) {
      lastSavedRef.current = sig;
      localStorage.setItem(storageKey, JSON.stringify(toSave));
    }
  }, [storageKey, filterList, excludeFromStorage]);

  useEffect(() => {
    const sig = JSON.stringify(filterOptions);
    if (sig !== lastSavedOptionsRef.current) {
      lastSavedOptionsRef.current = sig;
      localStorage.setItem(storageKeyOptions, JSON.stringify(filterOptions));
    }
  }, [storageKeyOptions, filterOptions]);

  useEffect(() => {
    if (!allowedFields || allowedFields.length === 0) return;

    const storedValues = parseStoredObject(localStorage.getItem(storageKey));
    const cleanedValues: AnyObj = {};
    Object.keys(storedValues).forEach((key) => {
      if (allowedFields.includes(key)) {
        cleanedValues[key] = storedValues[key];
      }
    });
    if (JSON.stringify(storedValues) !== JSON.stringify(cleanedValues)) {
      localStorage.setItem(storageKey, JSON.stringify(cleanedValues));
      setFilterList(cleanedValues as T);
    }

    const storedOptions = parseStoredObject(
      localStorage.getItem(storageKeyOptions),
    );
    const cleanedOptions: AnyObj = {};
    Object.keys(storedOptions).forEach((key) => {
      if (allowedFields.includes(key)) {
        cleanedOptions[key] = storedOptions[key];
      }
    });
    if (JSON.stringify(storedOptions) !== JSON.stringify(cleanedOptions)) {
      localStorage.setItem(storageKeyOptions, JSON.stringify(cleanedOptions));
      setFilterOptions(cleanedOptions);
    }

    const filterIdsRaw = localStorage.getItem(filterIdsKey);
    try {
      const parsed = JSON.parse(filterIdsRaw || "[]");
      if (Array.isArray(parsed)) {
        const cleanedIds = parsed.filter((id) => allowedFields.includes(id));
        if (cleanedIds.length !== parsed.length) {
          localStorage.setItem(filterIdsKey, JSON.stringify(cleanedIds));
        }
      }
    } catch {
      return;
    }
  }, [storageKey, storageKeyOptions, filterIdsKey, allowedFields]);

  useEffect(() => {
    const storedFilters = parseStoredObject(localStorage.getItem(storageKey));
    const filterIdsRaw = localStorage.getItem(filterIdsKey);

    setFilterList((prevFilterList) => {
      const next = { ...storedFilters } as T;

      let activeFilterIds: string[] = [];
      try {
        const parsed = JSON.parse(filterIdsRaw || "[]");
        activeFilterIds = Array.isArray(parsed) ? parsed : [];
      } catch {
        return prevFilterList;
      }

      activeFilterIds.forEach((filterId) => {
        if (filterId in defaultFilter && !(filterId in next)) {
          (next as AnyObj)[filterId] = defaultFilter[filterId];
        }
      });

      if (excludeFromStorage) {
        excludeFromStorage.forEach((field) => {
          if (prevFilterList[field] !== undefined) {
            (next as AnyObj)[field] = prevFilterList[field];
          }
        });
      }

      if (JSON.stringify(next) !== JSON.stringify(prevFilterList)) {
        return next;
      }
      return prevFilterList;
    });
  }, [storageKey, excludeFromStorage, filterIdsKey, defaultFilter]);

  useEffect(() => {
    const conditionalFieldsSet = new Set(
      fields?.filter((f) => f.conditional).map((f) => f.field) || [],
    );

    setFilterOptions((prevOptions) => {
      const updatedOptions = { ...prevOptions };
      let hasChanges = false;

      Object.keys(updatedOptions).forEach((key) => {
        if (conditionalFieldsSet.has(key)) return;

        const filterValue = filterList[key];
        const isEmpty =
          filterValue === null ||
          filterValue === undefined ||
          (Array.isArray(filterValue) && filterValue.length === 0);

        if (isEmpty && updatedOptions[key]) {
          delete updatedOptions[key];
          hasChanges = true;
        }
      });

      return hasChanges ? updatedOptions : prevOptions;
    });
  }, [filterList, fields]);

  return [filterList, setFilterList, filterOptions, setFilterOptions] as const;
}
